import React from 'react';

const TYPE_LABELS = {
  'article': 'Artículo',
  'book-chapter': 'Capítulo de libro',
  'conference-paper': 'Conferencia',
  'review': 'Revisión',
  'book': 'Libro',
  'dataset': 'Dataset',
  'preprint': 'Preprint',
};

const OA_COLORS = {
  'gold': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'green': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'hybrid': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'bronze': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  'closed': 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400',
};

function PublicacionCard({ pub, selected, onToggle, alreadySaved = false }) {
  const typeLabel = TYPE_LABELS[pub.type] || pub.type || 'Publicación';
  const oaColorClass = OA_COLORS[pub.oaStatus] || OA_COLORS['closed'];

  return (
    <div
      onClick={() => {
        if (alreadySaved) return; // No permitir seleccionar publicaciones ya existentes
        onToggle?.();
      }}
      className={`group relative flex gap-4 p-4 rounded-xl border-2 transition-all ${
        alreadySaved
          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 shadow-md cursor-not-allowed opacity-85'
          : selected
            ? 'border-[#2D5BA6] bg-blue-50 dark:bg-blue-900/10 shadow-md cursor-pointer hover:shadow-md'
            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md cursor-pointer'
      }`}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0 mt-0.5">
        <div
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            alreadySaved
              ? 'bg-emerald-500 border-emerald-500'
              : selected
                ? 'bg-[#2D5BA6] border-[#2D5BA6]'
                : 'border-slate-300 dark:border-slate-600 group-hover:border-[#2D5BA6]'
          }`}
        >
          {selected && !alreadySaved && (
            <span className="material-icons-round text-white text-sm">check</span>
          )}
          {alreadySaved && !selected && (
            <span className="material-icons-round text-white text-sm">done_all</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-full">
            {typeLabel}
          </span>
          {pub.year && (
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {pub.year}
            </span>
          )}
          {pub.oaStatus && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${oaColorClass}`}>
              {pub.oaStatus === 'closed' ? 'Cerrado' : pub.oaStatus.charAt(0).toUpperCase() + pub.oaStatus.slice(1)} OA
            </span>
          )}
          {pub.isTop10Percent && (
            <span className="text-xs font-semibold bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
              Top 10%
            </span>
          )}
        </div>

        <h3 className="font-semibold text-slate-900 dark:text-white text-sm leading-snug mb-2 line-clamp-3">
          {pub.title}
        </h3>

        {pub.journal && (
          <p className="text-xs text-slate-500 dark:text-slate-400 italic mb-2 line-clamp-1">
            {pub.journal}
            {pub.volume && `, Vol. ${pub.volume}`}
            {pub.issue && `, No. ${pub.issue}`}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-2">
          {pub.doi && (
            <a
              href={`https://doi.org/${pub.doi}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-xs text-[#2D5BA6] dark:text-[#5B8BD9] hover:underline"
            >
              <span className="material-icons-round text-sm">link</span>
              DOI
            </a>
          )}
          <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
            <span className="material-icons-round text-sm">format_quote</span>
            {pub.citationsCount || 0} citas
          </span>
          {pub.fwci && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              FWCI: {pub.fwci.toFixed(2)}
            </span>
          )}
          {pub.language && (
            <span className="text-xs text-slate-500 dark:text-slate-400 uppercase">
              {pub.language}
            </span>
          )}
          {alreadySaved && (
            <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 rounded-full">
              Ya en base
            </span>
          )}
        </div>

        {/* Authors preview */}
        {pub.authors && pub.authors.length > 0 && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-2 line-clamp-1">
            {pub.authors.slice(0, 3).map((a) => a.name).join(', ')}
            {pub.authors.length > 3 && ` +${pub.authors.length - 3} más`}
          </p>
        )}
      </div>
    </div>
  );
}

export default PublicacionCard;
